/**
 * Admin Integrations page — `/admin/integrations`
 *
 * Lets the agency owner paste Dropbox credentials directly from the
 * browser, persist them in MongoDB, and have the customer portal pick
 * them up immediately. Designed to bypass the deployment-platform's
 * .env-doesn't-auto-sync issue: production admin signs in here, pastes
 * the same keys preview uses, hits Save, runs Test → portal is live.
 *
 * Security:
 *   - Never displays current secret values in the form (only "configured"
 *     status). Admin must re-type the value to change it.
 *   - Env vars take precedence over DB values; this UI surfaces which
 *     source is currently active for each field.
 */

import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    Save,
    LogOut,
    CheckCircle2,
    AlertTriangle,
    Plug,
    PlugZap,
    ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal/admin/integrations`;

function Pill({ tone, children, ...rest }) {
    const palette = {
        green:
            "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
        amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
        muted: "border-white/15 bg-white/5 text-white/60",
    }[tone] || "border-white/15 bg-white/5 text-white/60";
    return (
        <span
            {...rest}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] ${palette}`}
        >
            {children}
        </span>
    );
}

function sourceTone(source) {
    if (source === "env") return "amber";
    if (source === "db") return "green";
    return "muted";
}
function sourceLabel(source) {
    if (source === "env") return "set via env var";
    if (source === "db") return "saved";
    return "not set";
}

export default function AdminIntegrationsPage() {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const authHeaders = React.useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);

    const [appKey, setAppKey] = useState("");
    const [appSecret, setAppSecret] = useState("");
    const [refreshToken, setRefreshToken] = useState("");
    const [rootCustomers, setRootCustomers] = useState("/Customers");

    const fetchStatus = React.useCallback(async () => {
        setError(null);
        try {
            const { data } = await axios.get(`${API}/dropbox`, {
                headers: authHeaders,
            });
            setStatus(data);
            setRootCustomers(data.root_customers || "/Customers");
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Could not load integration status. Check your admin session and try again."
            );
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    useEffect(() => {
        document.title = "Admin · Integrations — Endurance Casualty";
        fetchStatus();
    }, [fetchStatus]);

    const handleSignOut = () => {
        logout();
        navigate("/admin/login", { replace: true });
    };

    const handleSave = async (e) => {
        e?.preventDefault();
        setSaving(true);
        setError(null);
        setNotice(null);
        setTestResult(null);
        try {
            const body = {};
            if (appKey.trim()) body.app_key = appKey.trim();
            if (appSecret.trim()) body.app_secret = appSecret.trim();
            if (refreshToken.trim()) body.refresh_token = refreshToken.trim();
            if (rootCustomers !== status?.root_customers) {
                body.root_customers = rootCustomers || "/Customers";
            }
            if (Object.keys(body).length === 0) {
                setNotice("Nothing to save — fill in at least one field.");
                setSaving(false);
                return;
            }
            const { data } = await axios.post(`${API}/dropbox`, body, {
                headers: authHeaders,
            });
            setNotice(
                `Saved ${data.saved.length} value(s). ${
                    data.configured
                        ? "Dropbox is now configured."
                        : "Still missing one or more required values."
                }`
            );
            setAppKey("");
            setAppSecret("");
            setRefreshToken("");
            await fetchStatus();
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Could not save settings. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await axios.post(
                `${API}/dropbox/test`,
                {},
                { headers: authHeaders }
            );
            setTestResult(data);
        } catch (e) {
            setTestResult({
                ok: false,
                error:
                    e?.response?.data?.detail ||
                    "Test call failed for an unknown reason.",
            });
        } finally {
            setTesting(false);
        }
    };

    if (!token) return <Navigate to="/admin/login" replace />;

    return (
        <main
            data-testid="admin-integrations-page"
            className="min-h-screen bg-brand-bg text-brand-ivory"
        >
            <header className="border-b border-[rgba(212,175,55,0.18)] bg-[#05070a]">
                <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-6">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            Endurance Casualty · Admin
                        </p>
                        <h1 className="mt-1 font-serif text-2xl text-brand-ivory md:text-3xl">
                            Integrations
                        </h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <Link
                            to="/admin/videos"
                            data-testid="admin-link-videos"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            ← Videos
                        </Link>
                        <Link
                            to="/admin/blog"
                            data-testid="admin-link-blog"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Blog
                        </Link>
                        <Link
                            to="/admin/portal-users"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Portal Users
                        </Link>
                        <Link
                            to="/admin/analytics"
                            className="rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 hover:border-brand-gold hover:text-brand-gold sm:text-[11px]"
                        >
                            Analytics
                        </Link>
                        <button
                            type="button"
                            onClick={handleSignOut}
                            data-testid="admin-integrations-signout"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 hover:bg-brand-gold hover:text-brand-bg"
                        >
                            <LogOut aria-hidden="true" className="h-3 w-3" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-3xl px-6 py-10">
                {loading ? (
                    <div className="flex items-center gap-3 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-6 text-sm text-brand-muted">
                        <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                        Loading integration status…
                    </div>
                ) : (
                    <>
                        <div
                            data-testid="dropbox-status-card"
                            className="rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/40 bg-black/40">
                                        {status?.configured ? (
                                            <PlugZap className="h-5 w-5 text-emerald-300" />
                                        ) : (
                                            <Plug className="h-5 w-5 text-amber-300" />
                                        )}
                                    </span>
                                    <div>
                                        <h2 className="font-serif text-xl text-brand-ivory">
                                            Dropbox — Customer Portal documents
                                        </h2>
                                        <p className="mt-1 text-sm text-brand-muted">
                                            Connects the customer portal to your
                                            agency&apos;s Dropbox app folder. Every
                                            customer&apos;s documents live in{" "}
                                            <code className="rounded bg-black/40 px-1.5 py-0.5 text-xs text-brand-gold2">
                                                {status?.root_customers}/&lt;Customer
                                                ID&gt;
                                            </code>
                                            .
                                        </p>
                                    </div>
                                </div>
                                <Pill
                                    tone={status?.configured ? "green" : "amber"}
                                    data-testid="dropbox-status-pill"
                                >
                                    {status?.configured ? (
                                        <>
                                            <CheckCircle2 className="h-3 w-3" /> Live
                                        </>
                                    ) : (
                                        <>
                                            <AlertTriangle className="h-3 w-3" />{" "}
                                            Preview mode
                                        </>
                                    )}
                                </Pill>
                            </div>

                            <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-3">
                                <div>
                                    <dt className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                                        App Key
                                    </dt>
                                    <dd className="mt-1">
                                        <Pill
                                            tone={sourceTone(status?.app_key_source)}
                                            data-testid="dropbox-app-key-source"
                                        >
                                            {sourceLabel(status?.app_key_source)}
                                        </Pill>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                                        App Secret
                                    </dt>
                                    <dd className="mt-1">
                                        <Pill
                                            tone={sourceTone(
                                                status?.app_secret_source
                                            )}
                                            data-testid="dropbox-app-secret-source"
                                        >
                                            {sourceLabel(status?.app_secret_source)}
                                        </Pill>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                                        Refresh Token
                                    </dt>
                                    <dd className="mt-1">
                                        <Pill
                                            tone={sourceTone(
                                                status?.refresh_token_source
                                            )}
                                            data-testid="dropbox-refresh-token-source"
                                        >
                                            {sourceLabel(
                                                status?.refresh_token_source
                                            )}
                                        </Pill>
                                    </dd>
                                </div>
                            </dl>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={handleTest}
                                    disabled={testing}
                                    data-testid="dropbox-test-button"
                                    className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg disabled:opacity-50"
                                >
                                    {testing ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <PlugZap className="h-3.5 w-3.5" />
                                    )}
                                    Test connection
                                </button>
                                <a
                                    href="https://www.dropbox.com/developers/apps"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-muted hover:border-brand-gold/40 hover:text-brand-gold2"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Open Dropbox app console
                                </a>
                            </div>

                            {testResult && (
                                <div
                                    data-testid="dropbox-test-result"
                                    className={`mt-5 rounded-xl border p-4 text-sm ${
                                        testResult.ok
                                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                            : "border-rose-500/40 bg-rose-500/10 text-rose-200"
                                    }`}
                                >
                                    {testResult.ok ? (
                                        <>
                                            <p className="font-semibold">
                                                Connected to Dropbox ✓
                                            </p>
                                            <p className="mt-1 text-xs opacity-90">
                                                Account:{" "}
                                                <span className="font-mono">
                                                    {testResult.account
                                                        ?.display_name}{" "}
                                                    ({testResult.account?.email})
                                                </span>
                                            </p>
                                            {testResult.folders_at_root?.length >
                                            0 ? (
                                                <p className="mt-1 text-xs opacity-90">
                                                    Folders at app-folder root:{" "}
                                                    {testResult.folders_at_root.join(
                                                        ", "
                                                    )}
                                                </p>
                                            ) : (
                                                <p className="mt-1 text-xs opacity-90">
                                                    App folder is currently empty.
                                                    Create{" "}
                                                    <code className="rounded bg-black/30 px-1">
                                                        {status?.root_customers}/&lt;Customer
                                                        ID&gt;
                                                    </code>{" "}
                                                    folders inside Dropbox to start.
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <p className="font-semibold">
                                                Connection failed
                                            </p>
                                            <p className="mt-1 break-words text-xs opacity-90">
                                                {testResult.error}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <form
                            onSubmit={handleSave}
                            data-testid="dropbox-settings-form"
                            className="mt-8 rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] p-6"
                        >
                            <h3 className="font-serif text-lg text-brand-ivory">
                                Update credentials
                            </h3>
                            <p className="mt-1 text-xs text-brand-muted">
                                Leave a field blank to keep its current value. Pasting
                                a value here overrides what&apos;s saved in the database
                                (env vars, if set, still win on each request).
                            </p>

                            <div className="mt-5 space-y-4">
                                <Field
                                    label="App Key"
                                    helper="From the Dropbox app console — labelled 'App key'."
                                    value={appKey}
                                    onChange={setAppKey}
                                    placeholder="••• keep current •••"
                                    testid="dropbox-app-key-input"
                                />
                                <Field
                                    label="App Secret"
                                    helper="From the Dropbox app console — labelled 'App secret'."
                                    value={appSecret}
                                    onChange={setAppSecret}
                                    placeholder="••• keep current •••"
                                    testid="dropbox-app-secret-input"
                                    type="password"
                                />
                                <Field
                                    label="Refresh Token"
                                    helper="A long-lived token. Generate via the OAuth code-grant flow (token_access_type=offline)."
                                    value={refreshToken}
                                    onChange={setRefreshToken}
                                    placeholder="••• keep current •••"
                                    testid="dropbox-refresh-token-input"
                                    type="password"
                                />
                                <Field
                                    label="Customer Root Folder"
                                    helper="Path inside the App Folder where each customer's subfolder lives. Default: /Customers"
                                    value={rootCustomers}
                                    onChange={setRootCustomers}
                                    placeholder="/Customers"
                                    testid="dropbox-root-input"
                                />
                            </div>

                            {(notice || error) && (
                                <div
                                    role={error ? "alert" : "status"}
                                    className={`mt-5 rounded-xl border p-3 text-xs ${
                                        error
                                            ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
                                            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                    }`}
                                >
                                    {error || notice}
                                </div>
                            )}

                            <div className="mt-5 flex flex-wrap items-center gap-3">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="dropbox-save-button"
                                    className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg hover:bg-brand-gold2 disabled:opacity-50"
                                >
                                    {saving ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Save className="h-3.5 w-3.5" />
                                    )}
                                    Save changes
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </section>
        </main>
    );
}

function Field({ label, helper, value, onChange, placeholder, testid, type = "text" }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] text-brand-gold2">
                {label}
            </span>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete="off"
                spellCheck="false"
                className="mt-1 w-full rounded-lg border border-[rgba(212,175,55,0.25)] bg-[#05070a] px-3 py-2 font-mono text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:border-brand-gold focus:outline-none focus:ring-1 focus:ring-brand-gold"
            />
            {helper ? (
                <span className="mt-1 block text-[11px] text-brand-muted">
                    {helper}
                </span>
            ) : null}
        </label>
    );
}
